import {
  useEffect,
  useState,
  useContext,
  useCallback,
  useRef,
  LegacyRef,
} from 'react';
import getMobiusResponse from '@/utils/mobius/getMobiusResponse';
import { MobiusProgramResponse } from '@/types/Mobius';
import { GraphIcon } from '@/components/DesignSystem/Icons';
import getDcsOptions from '@/utils/mobius/getDcsOptions';
import { ContextualDefaults } from '@/types/enrichedContent';
import { MobiusContext } from '@/components/MobiusProvider';
import { SchoolInfoCard } from '@/components/DesignSystem/Cards/';
import { Pills } from '@/components/DesignSystem/Base/';
import { useDynamicDropdowns } from './utils/useDynamicDropdowns';
import tagularEvent from '@/utils/tagular/tagularEvent';
import uuid from '@/utils/uuid';
import EducationSelectionPanel from '@/components/DesignSystem/Subsections/SalaryCalculator/components/EducationSelectionPanel';
import { Degree, Category, Subject, Occupation, Program } from './types';
import OccupationCardGrid from './components/OccupationCardGrid';
import SalaryIncreasePanel from '@/components/DesignSystem/Subsections/SalaryCalculator/components/SalaryIncreasePanel';
import SalaryInsights from '@/components/DesignSystem/Subsections/SalaryCalculator/components/SalaryInsights';
import getUrlQuery from '@/utils/getUrlQuery';
import getTrafficSource from '@/utils/getTrafficSource';
import getAliasParams from '@/utils/getAliasParams';
import { TagularProduct, TagularWebElement } from '@/types/Tagular';
import useViewEvents from '@/hooks/useViewEvent';
import { getNumericId } from './utils/salaryDataProcessor';
import Disclosure from '@/components/Tooltip/Disclosure';
import { isVoyagerCombo } from '@/utils/isVoyagerCombo';
import { getCurrentFormStateNames } from '@/utils/formStateUtils';
import useFlowTrackingEvents from '@/components/DynamicFlow/hooks/useFlowTrackingEvent';
import { useDynamicExperienceContext } from '@/components/DynamicFlow/providers/DynamicExperienceProvider';

export type Occupations = {
  occupations: Occupation[];
  loading: boolean;
  error: string | null;
};

const SalaryCalculator = () => {
  const viewCorrelationIdRef = useRef(uuid());
  const { eventContext } = useDynamicExperienceContext();
  const { triggerProductClickedEvent } = useFlowTrackingEvents();
  // Config Variables
  const { degrees, presets, siteConfig } = useContext(MobiusContext);
  const contextualDefaults = { ...presets };
  const identityToken = siteConfig.identityToken;
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  // Get opitons for DCS form
  const options = getDcsOptions(
    contextualDefaults as ContextualDefaults,
    degrees || [],
  );

  // State Variables
  // Variables for list of categories and list of subjects for the form
  const [categories, setCategories] = useState<Category[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);

  // Selected Options States (set defaults via Presets)
  const [selectedCategory, setSelectedCategory] = useState<string>();
  const [selectedSubject, setSelectedSubject] = useState<string>();
  const [selectedDegree, setSelectedDegree] = useState<string>(); // Default to bachelor's degree (ID: 2)
  const [inputtedSalary, setInputtedSalary] = useState<number | null>(null);
  const [selectedOccupationCard, setSelectedOccupationCard] =
    useState<Occupation | null>(null);

  // Track latest D/C/S so we can ignore stale Mobius responses
  const latestDcsRef = useRef({
    degree: '',
    category: '',
    subject: '',
  });

  // Track previous category to detect actual category changes
  const prevCategoryRef = useRef<string | undefined>(undefined);

  // Track previous subject to detect actual subject changes
  const prevSubjectRef = useRef<string | undefined>(undefined);

  // Recommended Schools States
  const [loadingPrograms, setLoadingPrograms] = useState<boolean>(false);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [occupations, setOccupations] = useState<string[]>([]);
  const [occupationObjects, setOccupationObjects] = useState<Occupation[]>([]);

  const initialValues = {
    degree: contextualDefaults.degree || 'bachelors', // Default to bachelor's degree
    category: contextualDefaults.category,
    subject: contextualDefaults.subject,
  };

  // Get options and form state for education selection panel
  const { formState, handleFieldChange } = useDynamicDropdowns(initialValues);

  // If there are presets, set initial selections based on them
  useEffect(() => {
    if (presets?.degree) {
      setSelectedDegree(presets.degree);
    }

    if (presets?.category) {
      setSelectedCategory(presets.category);
    }

    if (presets?.subject && options?.subject) {
      setSelectedSubject(presets.subject);
    }

    if (options?.subject) {
      setSubjects(options.subject);
    }
  }, [presets, options?.subject]);

  // Keep latest D/C/S snapshot updated for in-flight request checks
  useEffect(() => {
    latestDcsRef.current = {
      degree: selectedDegree || '',
      category: selectedCategory || '',
      subject: selectedSubject || '',
    };
  }, [selectedDegree, selectedCategory, selectedSubject]);

  // Fetch programs from mobius based on DCS selections
  const fetchPrograms = useCallback(async () => {
    // only get programs if all options are selected
    if (!selectedSubject || !selectedCategory || !selectedDegree) {
      // Clear programs if selections are not complete
      setPrograms([]);
      setOccupationObjects([]);
      return;
    }

    const subjectId = getNumericId(
      'subject',
      selectedSubject,
      formState.options,
    );
    const categoryId = getNumericId(
      'category',
      selectedCategory,
      formState.options,
    );
    const degreeId = getNumericId('degree', selectedDegree, formState.options);

    // Clear existing results before fetching new ones so we don't show stale
    // Recommended Programs while loading updated matches for a new subject
    setPrograms([]);
    setOccupationObjects([]);

    // If any ID is 0, options are not yet fully populated or mapping failed
    if (!subjectId || !categoryId || !degreeId) {
      return;
    }

    // Snapshot the D/C/S for this specific request
    const requestDcs = { ...latestDcsRef.current };

    setLoadingPrograms(true);
    try {
      const response = (await getMobiusResponse(
        'criteriabased',
        identityToken,
        {
          criteria: {
            subjectId,
            categoryId,
            degreeId,
            limit: 3,
          },
        },
      )) as MobiusProgramResponse;

      // If D/C/S changed while this request was in flight, ignore this stale response
      const latest = latestDcsRef.current;
      if (
        latest.degree !== requestDcs.degree ||
        latest.category !== requestDcs.category ||
        latest.subject !== requestDcs.subject
      ) {
        return;
      }

      if (!response) {
        setPrograms([]);
        setOccupationObjects([]);
      } else if (response.length < 1) {
        setPrograms([]);
        setOccupationObjects([]);
      } else {
        // Get necessary fields to display in the program cards
        const mappedPrograms: Program[] = response.map((product: any) => ({
          id: product?.product?.program.id,
          name: product?.product?.program.name,
          description:
            product?.product?.program?.snippet ||
            product?.product?.program?.description ||
            product?.product?.school?.snippet ||
            product?.product?.school?.description,
          image: product?.image,
          schoolName:
            product?.product?.school?.name || product?.school_name || '',
          degreeLevel:
            product?.product?.program?.degree?.name ||
            product?.degree_level ||
            '',
          url: product?.product?.destination?.url,
          schoolSlug: product?.product?.school?.slug || '',
          capId: product?.metadata?.capId
            ? product?.metadata?.capId.toString()
            : '',
          requestId: product?.requestId || '',
          helid: product?.product?.destination.helid || '',
          related: product?.related || false,
        }));

        // Initialize an array to hold all unique occupation codes from each proram
        const allOccupationCodes: string[] = [];
        response.forEach((product: any) => {
          // Check if product has occupation codes
          if (
            product.product?.program?.occupationCodes &&
            Array.isArray(product.product.program.occupationCodes)
          ) {
            // Add all unique occupation codes from this program
            product.product.program.occupationCodes.forEach((code: string) => {
              if (!allOccupationCodes.includes(code)) {
                allOccupationCodes.push(code);
              }
            });
          }
        });

        // Update state for list of occupation codes and programs
        setOccupations(allOccupationCodes);
        setPrograms(mappedPrograms);
        fetchOccupations(allOccupationCodes);
      }
    } catch (error) {
      console.error('Error fetching Mobius programs:', error);
      setPrograms([]);
      setOccupationObjects([]);
    } finally {
      setLoadingPrograms(false);
    }
  }, [
    selectedCategory,
    selectedDegree,
    selectedSubject,
    identityToken,
    formState.options,
  ]);

  // Fetch Programs if all dcs selection are set
  useEffect(() => {
    if (selectedDegree && selectedCategory && selectedSubject) {
      fetchPrograms();
    }
  }, [selectedDegree, selectedCategory, selectedSubject, fetchPrograms]);

  // Update categories when options.degree changes or when selectedDegree changes
  useEffect(() => {
    if (options?.degree && options.degree.length > 0 && selectedDegree) {
      const selectedDegreeObj = options.degree.find(
        (d: Degree) => d.slug === selectedDegree,
      );
      if (selectedDegreeObj && selectedDegreeObj.categories) {
        setCategories(selectedDegreeObj.categories);

        // Reset subject when degree changes
        setSelectedSubject('');
      } else {
        setCategories([]);
      }
    }
  }, [options.degree, selectedDegree]);

  // Clear programs/occupations and update subjects when selectedCategory changes
  useEffect(() => {
    // Detect actual category change (ignore initial undefined -> preset transition)
    if (
      prevCategoryRef.current !== undefined &&
      prevCategoryRef.current !== selectedCategory
    ) {
      setPrograms([]);
      setOccupationObjects([]);
    }
    prevCategoryRef.current = selectedCategory;

    if (selectedCategory === '' || !categories.length) return;

    // Find the selected category by slug
    const category = categories.find((cat) => cat.slug === selectedCategory);

    const categorySubjects = category?.subjects ?? [];

    if (categorySubjects.length > 0) {
      // Set the subjects from the selected category
      setSubjects(categorySubjects);
    } else {
      // Reset subjects if no matching category is found
      setSubjects([]);
    }
  }, [selectedCategory, categories]);

  // Clear programs/occupations when subject changes
  useEffect(() => {
    // Skip on very first render, only react to *actual* subject changes
    if (
      prevSubjectRef.current !== undefined &&
      prevSubjectRef.current !== selectedSubject
    ) {
      setPrograms([]);
      setOccupationObjects([]);
    }

    prevSubjectRef.current = selectedSubject;
  }, [selectedSubject]);

  // fetch occupations after fetching programs and occupations list is set
  const fetchOccupations = async (occupations: string[]) => {
    if (!occupations.length) return;
    setLoading(true);
    setError(null);

    try {
      const proxy = process.env.NEXT_PUBLIC_PROXY_OCCUPATIONS_SERVICE || '';
      const endpoint = `${proxy}/occupations`;
      // Create promises for all occupation requests
      const occupationPromises = occupations
        .filter((occupation) => occupation) // Filter out empty strings
        .map(async (occupation) => {
          try {
            const response = await fetch(endpoint, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                include: ['employmentAndWageStatistics', 'projections'],
                filter: {
                  onetSocCode: occupation,
                },
              }),
            });

            const data = await response.json();

            // Only return data if it has average salary information
            if (
              data.data &&
              data.data[0] &&
              data.data[0].employmentAndWageStatistics?.meanAnnualWage
            ) {
              return {
                description: data.data[0].description || '',
                title: data.data[0].title || '',
                onetSocCode: data.data[0].onetSocCode,
                salary:
                  data.data[0].employmentAndWageStatistics?.meanAnnualWage || 0,
                employmentChange:
                  data.data[0].projections?.employmentChange2023,
              } as Occupation;
            }
            return null; // Return null for invalid data
          } catch (err) {
            console.error(`Error fetching occupation ${occupation}:`, err);
            return null; // Return null for failed requests
          }
        });

      // Wait for all promises to resolve
      const results = await Promise.all(occupationPromises);

      // Filter out null results and zero salaries
      const allOccupationData = results.filter(
        (result): result is NonNullable<typeof result> =>
          result !== null && result.salary > 0,
      );
      // Sort by salary (ascending)
      allOccupationData.sort((a, b) => a.salary - b.salary);

      // Create the final array with categorized salaries
      let processedSalaries: Occupation[] = [];

      // if there are 6 or fewer valid salaries, just use them all
      if (allOccupationData.length <= 6) {
        processedSalaries = allOccupationData.map((salary) => ({
          ...salary,
        }));
      } else {
        // Add 2 lowest salaries
        if (allOccupationData.length >= 1) {
          const lowest1 = { ...allOccupationData[0] };
          processedSalaries.push(lowest1);

          if (allOccupationData.length >= 2) {
            const lowest2 = { ...allOccupationData[1] };
            processedSalaries.push(lowest2);
          }
        }

        // Add median salaries
        if (allOccupationData.length >= 3) {
          const medianIndex = Math.floor(allOccupationData.length / 2);

          if (allOccupationData.length % 2 === 0) {
            // Even length - get two middle values
            const median1 = {
              ...allOccupationData[medianIndex - 1],
            };
            const median2 = { ...allOccupationData[medianIndex] };
            processedSalaries.push(median1, median2);
          } else {
            // Odd length - get middle value and one adjacent
            const median1 = {
              ...allOccupationData[medianIndex - 1],
            };
            const median2 = { ...allOccupationData[medianIndex] };
            processedSalaries.push(median1, median2);
          }
        }

        // Add 2 highest salaries
        if (allOccupationData.length >= 1) {
          const highest1 = {
            ...allOccupationData[allOccupationData.length - 1],
          };
          processedSalaries.push(highest1);

          if (allOccupationData.length >= 2) {
            const highest2 = {
              ...allOccupationData[allOccupationData.length - 2],
            };
            processedSalaries.push(highest2);
          }
        }
      }

      setOccupationObjects(processedSalaries);
      setSelectedOccupationCard(
        processedSalaries.length > 0 ? processedSalaries[0] : null,
      );
    } catch (error) {
      console.error('Error in fetchOccupations:', error);
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  // First, let's enhance the onClick handler to handle tracking and navigation
  const handleCtaButtonClick = (type: string) => {
    const correlationId = uuid();
    const isStudyMatch = type === 'studymatch';

    // Determine the correct URL
    const targetUrl = isStudyMatch
      ? siteConfig?.studyMatchUrl
      : siteConfig?.voyagerUrl;

    // Get current form field values
    const currentValues = {
      degree: selectedDegree || '',
      category: selectedCategory || '',
      subject: selectedSubject || '',
    };

    const isVoyagerComboResult = isVoyagerCombo(currentValues);

    // Get formatted names for Voyager URLs
    const currentLabels = getCurrentFormStateNames(formState);

    // Create base query parameters for outbound url
    const baseQueryParams = {
      publisher: siteConfig?.domain,
      correlationId,
      idToken: siteConfig?.identityToken,
      position: 'null',
      source: getTrafficSource(window.location.href),
      ...getAliasParams(),
    };

    let outboundUrl: string;

    if (isStudyMatch || !isVoyagerComboResult) {
      // For StudyMatch or non-Voyager combos, use regular parameters
      const studyMatchParams = {
        ...baseQueryParams,
        degree: currentValues.degree,
        category: currentValues.category,
        subject: currentValues.subject,
      };
      outboundUrl = getUrlQuery(targetUrl, studyMatchParams);
    } else {
      // For Voyager combos, use the formatted DCS parameters
      const voyagerParams = {
        ...baseQueryParams,
        'dcs[degrees][]': currentLabels.degree || currentValues.degree,
        'dcs[categories][]': currentLabels.category || currentValues.category,
        'dcs[subjects][]': currentLabels.subject || currentValues.subject,
      };
      outboundUrl = getUrlQuery(targetUrl, voyagerParams);
    }

    if (!eventContext.dynamicExperienceEventFlag) {
      // Create the web element for tracking
      const buttonWebElement: TagularWebElement = {
        elementType: isStudyMatch ? 'Link' : 'widget',
        htmlId: isStudyMatch ? 'view-more-button' : 'voyager_dcs',
        location: 'salary-calculator',
        name: `calculator-${type}-entry`,
        position: isStudyMatch ? 'Below Editorial Listings' : 'null',
        text: isStudyMatch ? 'View More Programs' : 'Take Our Quiz',
      };

      const basePayloadForProductClicked: TagularProduct = {
        brand: '',
        category: selectedCategory || '',
        formatSubtype: 'calculator-dcs',
        formatType: 'widget',
        location: 'salary-calculator',
        name: selectedSubject || '',
        positionEngine: 'null',
        variant: selectedDegree || '',
      };

      // Create the tracking event payload
      const clickEventPayload = {
        name: 'ElementClicked',
        payload: {
          webElement: buttonWebElement,
          outboundUrl: outboundUrl || '',
          actionOutcome: 'INTERNALLINK',
          correlationId: correlationId,
        },
      };

      const productClickedPayload = {
        name: 'ProductClicked',
        payload: {
          outboundUrl: outboundUrl || '',
          actionOutcome: 'INTERNALLINK',
          correlationId: correlationId,
          product: basePayloadForProductClicked,
          viewCorrelationId: viewCorrelationIdRef.current,
        },
      };

      // Fire the Tagular tracking event
      tagularEvent('ElementClicked', clickEventPayload.payload);

      if (!isStudyMatch) {
        tagularEvent('ProductClicked', productClickedPayload.payload);
      }
    } else {
      triggerProductClickedEvent({
        ...eventContext,
        presets,
        dcs: {
          degree: selectedDegree || '',
          category: selectedCategory || '',
          subject: selectedSubject || '',
        },
        customDimensions: [
          ...(eventContext.customDimensions || []),
          {
            key: 'ctaText',
            value: isStudyMatch ? 'View More Programs' : 'Find Your Match',
          },
        ],
      });
    }

    // Navigate to the URL
    if (targetUrl) {
      window.open(outboundUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const viewCorrelationId = viewCorrelationIdRef.current;
  const baseViewEventPayload: TagularWebElement = {
    elementType: 'widget',
    htmlId: 'salary-calculator',
    location: 'salary-calculator',
    name: 'salary-calculator',
    position: '',
    text: 'Salary Calculator',
  };

  const { ref } = useViewEvents([
    {
      name: 'ElementViewed',
      payload: { ...baseViewEventPayload, viewCorrelationId },
    },
  ]);

  return (
    <div
      ref={ref as LegacyRef<HTMLDivElement>}
      className='not-prose bg-white grid grid-cols-1 mx-auto p-4 md:p-6 border shadow-small rounded-small border-gray-50 gap-4 md:gap-6 w-full max-w-[1220px]'
    >
      <div className='flex gap-4'>
        <div className='bg-primary-50 rounded w-fit p-2 h-fit'>
          <div className='w-full h-auto max-w-8 max-h-8 text-primary-500'>
            <GraphIcon />
          </div>
        </div>
        <div>
          <div className='font-heading-h7 md:font-heading-h5 font-normal'>
            Salary Comparison
          </div>
          <p className='text-heading-secondary font-body-14'>
            Compare potential salaries across different education levels in your
            field
          </p>
        </div>
      </div>

      <div className='grid grid-cols-1 md:grid-cols-3 md:gap-8'>
        {/* Selection Panel */}
        <EducationSelectionPanel
          selectedDegree={selectedDegree || ''}
          selectedCategory={selectedCategory || ''}
          selectedSubject={selectedSubject || ''}
          inputtedSalary={inputtedSalary}
          formState={formState}
          setSelectedDegree={setSelectedDegree}
          setSelectedCategory={setSelectedCategory}
          setSelectedSubject={setSelectedSubject}
          setSubjects={setSubjects}
          setInputtedSalary={setInputtedSalary}
          handleFieldChange={handleFieldChange}
        />
        {/* END Selection Panel */}
        <div className='col-span-2'>
          <OccupationCardGrid
            occupationSalaries={occupationObjects}
            loading={loading}
            selectedOccupationCard={selectedOccupationCard}
            setSelectedOccupationCard={setSelectedOccupationCard}
          />
          {/* Potential Salary Increase */}
          <SalaryIncreasePanel
            inputtedSalary={inputtedSalary ?? 0}
            selectedOccupationCard={selectedOccupationCard}
          />
          {/* Salary Insights */}
          <SalaryInsights selectedOccupationCard={selectedOccupationCard} />
        </div>
      </div>
      {/* New Section: Recommended Programs */}
      <div className='mt-4 md:mt-6'>
        <div className='font-body-20 font-bold mb-4'>Recommended Programs</div>

        {/* Subject Filter Pills */}
        <Pills
          items={subjects}
          selectedItem={selectedSubject || ''}
          setSelectedItem={setSelectedSubject}
          handleFieldChange={handleFieldChange}
          fieldName='subject'
        />

        {/* School Cards - remains mostly the same */}
        <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
          {programs.length > 0 ? (
            programs.map((school, index) => (
              <SchoolInfoCard
                key={school.name}
                program={{
                  degreeLevel: school.degreeLevel || 'Degree',
                  name: school.name,
                  description: school.description || '',
                  image: school.image || '',
                  ctaText: 'Learn More',
                  altText: `${school.schoolName} program logo`,
                  url: school?.url || '',
                  schoolSlug: school.schoolSlug || '',
                  id: school.id,
                  schoolName: school.schoolName || '',
                  capId: school.capId || '',
                  requestId: school.requestId || '',
                  helid: school.helid || '',
                  related: school.related || false,
                }}
                selectedCategory={selectedCategory}
                selectedSubject={selectedSubject}
                selectedDegree={selectedDegree}
                index={index} // Pass index for unique key
              />
            ))
          ) : (
            <div className='col-span-3 text-center py-8 text-gray-500'>
              No programs found matching your criteria. Try adjusting your
              filters.
            </div>
          )}
        </div>

        {/* View All Programs Button */}
        <div className='flex flex-col md:flex-row justify-center text-center mt-8 gap-4'>
          <button
            onClick={() => handleCtaButtonClick('studymatch')}
            className='bg-secondary-700 text-white px-6 py-2 rounded-md text-sm hover:bg-secondary-800'
          >
            View More Programs
          </button>
          <button
            onClick={() => handleCtaButtonClick('voyager')}
            className='bg-primary-700 text-white px-6 py-2 rounded-md text-sm hover:bg-primary-800'
          >
            Find Your Match
          </button>
        </div>
        <div className='flex justify-center items-center my-4'>
          <Disclosure />
        </div>
      </div>
    </div>
  );
};

export default SalaryCalculator;
